import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  StyleSheet,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Platform,
} from "react-native";
import { useFirebase } from "@components/firebase/useFirebase";
import { getMyContacts } from "@/services/firebase/user-functions";
import { getMyGroups } from "@/services/firebase/group-functions";
import { ContactModel, GroupModel } from "common247";
import { useThemeColor } from "@/hooks/useThemeColor";
import { IconSymbol } from "@/components/ui/IconSymbol";

export interface ISelectedRecipient {
  targetType: "contact" | "group";
  targetId: string;
  targetName: string;
  recipientUserId?: string;
  isShared: boolean;
  userRole?: string;
  label: string;
}

interface IRecipientSelectorProps {
  selectedRecipient: ISelectedRecipient | null;
  onSelect: (recipient: ISelectedRecipient) => void;
  error?: string | null;
}

type FilterCategory = "all" | "contacts" | "groups" | "yours" | "shared";

/**
 * Recipient selector component for the message composer.
 * Retrieves and lists all contacts (owned and shared) and all groups (owned and shared).
 * Provides category filtering and instant keyword search.
 */
export function RecipientSelector({
  selectedRecipient,
  onSelect,
  error,
}: IRecipientSelectorProps) {
  const services = useFirebase();

  const [contacts, setContacts] = useState<ContactModel[]>([]);
  const [groups, setGroups] = useState<GroupModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [activeCategory, setActiveCategory] = useState<FilterCategory>("all");
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const containerRef = useRef<View | null>(null);

  // Close dropdown on outside click (Web)
  useEffect(() => {
    if (!isExpanded) return undefined;
    if (Platform.OS === "web" && typeof document !== "undefined") {
      const handleOutsideClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const container = containerRef.current as any;
        if (container && typeof container.contains === "function") {
          if (!container.contains(target)) {
            setIsExpanded(false);
          }
        }
      };
      document.addEventListener("mousedown", handleOutsideClick);
      return () => {
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    }
    return undefined;
  }, [isExpanded]);

  // Theme colors
  const cardBg = useThemeColor({}, "card");
  const textColor = useThemeColor({}, "text");
  const inputTextColor = useThemeColor({}, "inputText");
  const borderColor = useThemeColor({}, "border");
  const primaryTint = useThemeColor({}, "tint");
  const subtextColor = useThemeColor({}, "tabIconDefault");

  useEffect(() => {
    let isCancelled = false;

    async function loadRecipients() {
      if (!services) return;
      setIsLoading(true);
      try {
        const [contactsRes, groupsRes] = await Promise.all([
          getMyContacts(services, { includeInactive: false }),
          getMyGroups(services, {}),
        ]);

        if (!isCancelled) {
          if (contactsRes && contactsRes.contacts) {
            setContacts(contactsRes.contacts);
          }
          if (groupsRes && groupsRes.groups) {
            setGroups(groupsRes.groups);
          }
        }
      } catch (err) {
        console.warn("Failed to load contacts and groups for recipient list:", err);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    loadRecipients();

    return () => {
      isCancelled = true;
    };
  }, [services]);

  // Transform into unified recipient items
  const allRecipientItems = useMemo<ISelectedRecipient[]>(() => {
    const list: ISelectedRecipient[] = [];

    // 1. Process contacts
    for (const c of contacts) {
      const isOwner = c.userRole === "owner";
      const name = c.displayName || c.email || "Unnamed Contact";
      const roleText = isOwner
        ? "Yours (Owner)"
        : `Shared (${c.ownerDisplayName ? `by ${c.ownerDisplayName}` : c.userRole || "Viewer"})`;

      list.push({
        targetType: "contact",
        targetId: c.id || c.email || "",
        targetName: name,
        recipientUserId: c.userId,
        isShared: !isOwner,
        userRole: c.userRole || (isOwner ? "owner" : "viewer"),
        label: `${name} • ${roleText}`,
      });
    }

    // 2. Process groups
    for (const g of groups) {
      const isOwner = g.userRole === "owner";
      const name = g.name || "Unnamed Group";
      const roleText = isOwner
        ? "Yours (Owner)"
        : `Shared (${g.ownerDisplayName ? `by ${g.ownerDisplayName}` : g.userRole || "Viewer"})`;

      list.push({
        targetType: "group",
        targetId: g.id || "",
        targetName: name,
        isShared: !isOwner,
        userRole: g.userRole || (isOwner ? "owner" : "viewer"),
        label: `${name} • ${roleText}`,
      });
    }

    return list;
  }, [contacts, groups]);

  // Filter items by category and query
  const filteredRecipients = useMemo(() => {
    return allRecipientItems.filter((item) => {
      // Category filter
      if (activeCategory === "contacts" && item.targetType !== "contact") return false;
      if (activeCategory === "groups" && item.targetType !== "group") return false;
      if (activeCategory === "yours" && item.isShared) return false;
      if (activeCategory === "shared" && !item.isShared) return false;

      // Search query filter
      if (searchQuery.trim() !== "") {
        const q = searchQuery.toLowerCase();
        const matchName = item.targetName.toLowerCase().includes(q);
        const matchLabel = item.label.toLowerCase().includes(q);
        return matchName || matchLabel;
      }

      return true;
    });
  }, [allRecipientItems, activeCategory, searchQuery]);

  const handleSelectItem = (item: ISelectedRecipient) => {
    onSelect(item);
    setIsExpanded(false);
  };

  return (
    <View ref={containerRef} style={styles.container}>
      <Text style={[styles.fieldLabel, { color: textColor }]}>
        Recipient <Text style={styles.requiredAsterisk}>*</Text>
      </Text>

      {/* Trigger Button showing selected recipient or prompt */}
      <TouchableOpacity
        activeOpacity={0.8}
        onPress={() => setIsExpanded(!isExpanded)}
        style={[
          styles.triggerButton,
          {
            backgroundColor: cardBg,
            borderColor: error ? "#DC2626" : borderColor,
          },
        ]}
      >
        <View style={styles.triggerContent}>
          {selectedRecipient ? (
            <View style={styles.selectedRow}>
              <View
                style={[
                  styles.targetBadge,
                  {
                    backgroundColor:
                      selectedRecipient.targetType === "contact" ? "#EFF6FF" : "#F0FDF4",
                  },
                ]}
              >
                <IconSymbol
                  size={16}
                  name={
                    selectedRecipient.targetType === "contact"
                      ? "person.fill"
                      : "person.3.fill"
                  }
                  color={
                    selectedRecipient.targetType === "contact" ? "#2563EB" : "#16A34A"
                  }
                />
                <Text
                  style={[
                    styles.targetBadgeText,
                    {
                      color:
                        selectedRecipient.targetType === "contact"
                          ? "#2563EB"
                          : "#16A34A",
                    },
                  ]}
                >
                  {selectedRecipient.targetType === "contact" ? "Contact" : "Group"}
                </Text>
              </View>

              <Text style={[styles.selectedName, { color: textColor }]} numberOfLines={1}>
                {selectedRecipient.targetName}
              </Text>

              <View
                style={[
                  styles.ownershipBadge,
                  {
                    backgroundColor: selectedRecipient.isShared
                      ? "#FEF3C7"
                      : "#E0E7FF",
                  },
                ]}
              >
                <Text
                  style={[
                    styles.ownershipBadgeText,
                    {
                      color: selectedRecipient.isShared ? "#92400E" : "#3730A3",
                    },
                  ]}
                >
                  {selectedRecipient.isShared ? "Shared" : "Yours"}
                </Text>
              </View>
            </View>
          ) : (
            <Text style={[styles.placeholderText, { color: subtextColor }]}>
              Select contact or group (yours & shared)...
            </Text>
          )}
        </View>

        <IconSymbol
          size={18}
          name={isExpanded ? "slider.horizontal.3" : "chevron.right"}
          color={subtextColor}
        />
      </TouchableOpacity>

      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      {/* Expanded dropdown panel */}
      {isExpanded && (
        <View
          style={[
            styles.dropdownPanel,
            {
              backgroundColor: cardBg,
              borderColor: borderColor,
            },
          ]}
        >
          {/* Search Input */}
          <View style={[styles.searchBox, { borderColor: borderColor }]}>
            <TextInput
              style={[styles.searchInput, { color: inputTextColor }]}
              placeholder="Search contacts and groups..."
              placeholderTextColor={subtextColor}
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoFocus
            />
          </View>

          {/* Quick Filter Categories */}
          <View style={styles.categoryTabs}>
            {(
              [
                { id: "all", label: "All" },
                { id: "contacts", label: "Contacts" },
                { id: "groups", label: "Groups" },
                { id: "yours", label: "Yours" },
                { id: "shared", label: "Shared" },
              ] as const
            ).map((cat) => (
              <TouchableOpacity
                key={cat.id}
                onPress={() => setActiveCategory(cat.id)}
                style={[
                  styles.categoryTab,
                  activeCategory === cat.id && {
                    backgroundColor: primaryTint,
                    borderColor: primaryTint,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.categoryTabText,
                    {
                      color:
                        activeCategory === cat.id ? "#FFFFFF" : subtextColor,
                    },
                  ]}
                >
                  {cat.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Recipient list */}
          {isLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="small" color={primaryTint} />
              <Text style={[styles.loadingText, { color: subtextColor }]}>
                Loading contacts and groups...
              </Text>
            </View>
          ) : filteredRecipients.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={[styles.emptyText, { color: subtextColor }]}>
                No matching contacts or groups found.
              </Text>
            </View>
          ) : (
            <ScrollView style={styles.listScrollView} nestedScrollEnabled>
              {filteredRecipients.map((item) => {
                const isSelected =
                  selectedRecipient?.targetId === item.targetId &&
                  selectedRecipient?.targetType === item.targetType;

                return (
                  <TouchableOpacity
                    key={`${item.targetType}-${item.targetId}`}
                    activeOpacity={0.7}
                    onPress={() => handleSelectItem(item)}
                    style={[
                      styles.listItem,
                      { borderColor: borderColor },
                      isSelected && {
                        backgroundColor: "rgba(74, 144, 226, 0.12)",
                      },
                    ]}
                  >
                    <View style={styles.itemLeft}>
                      <View
                        style={[
                          styles.itemIconCircle,
                          {
                            backgroundColor:
                              item.targetType === "contact"
                                ? "#DBEAFE"
                                : "#DCFCE7",
                          },
                        ]}
                      >
                        <IconSymbol
                          size={16}
                          name={
                            item.targetType === "contact"
                              ? "person.fill"
                              : "person.3.fill"
                          }
                          color={
                            item.targetType === "contact" ? "#1D4ED8" : "#15803D"
                          }
                        />
                      </View>

                      <View style={styles.itemDetails}>
                        <View style={styles.itemHeaderRow}>
                          <Text
                            style={[styles.itemName, { color: textColor }]}
                            numberOfLines={1}
                          >
                            {item.targetName}
                          </Text>

                          <View
                            style={[
                              styles.smallPill,
                              {
                                backgroundColor: item.isShared
                                  ? "#FEF3C7"
                                  : "#E0E7FF",
                              },
                            ]}
                          >
                            <Text
                              style={[
                                styles.smallPillText,
                                {
                                  color: item.isShared
                                    ? "#92400E"
                                    : "#3730A3",
                                },
                              ]}
                            >
                              {item.isShared ? "Shared" : "Yours"}
                            </Text>
                          </View>
                        </View>

                        <Text
                          style={[styles.itemSubtext, { color: subtextColor }]}
                          numberOfLines={1}
                        >
                          {item.label}
                        </Text>
                      </View>
                    </View>

                    {isSelected && (
                      <IconSymbol size={18} name="checkmark" color={primaryTint} />
                    )}
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
    position: "relative",
    zIndex: 100,
  },
  fieldLabel: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: "600",
    marginBottom: 2,
    opacity: 0.75,
    letterSpacing: 0.2,
  },
  requiredAsterisk: {
    color: "#AB1717",
  },
  triggerButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderWidth: 1,
    borderRadius: 0,
    paddingHorizontal: 12,
    paddingVertical: 8,
    minHeight: 36,
  },
  triggerContent: {
    flex: 1,
    marginRight: 8,
  },
  selectedRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  targetBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 0,
  },
  targetBadgeText: {
    fontSize: 11,
    fontWeight: "700",
  },
  selectedName: {
    fontSize: 14,
    fontWeight: "600",
    flex: 1,
  },
  ownershipBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 0,
  },
  ownershipBadgeText: {
    fontSize: 11,
    fontWeight: "700",
  },
  placeholderText: {
    fontSize: 14,
  },
  dropdownPanel: {
    position: "absolute",
    top: 72,
    left: 0,
    right: 0,
    zIndex: 1000,
    borderWidth: 1,
    borderRadius: 0,
    padding: 12,
    maxHeight: 280,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.2,
    shadowRadius: 10,
    elevation: 12,
  },
  searchBox: {
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginBottom: 8,
  },
  searchInput: {
    fontSize: 13,
    padding: 0,
  },
  categoryTabs: {
    flexDirection: "row",
    gap: 6,
    marginBottom: 10,
  },
  categoryTab: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 0,
    borderWidth: 1,
    borderColor: "rgba(128, 128, 128, 0.3)",
  },
  categoryTabText: {
    fontSize: 11,
    fontWeight: "600",
  },
  listScrollView: {
    maxHeight: 220,
  },
  listItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 0,
    borderBottomWidth: 1,
  },
  itemLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flex: 1,
  },
  itemIconCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  itemDetails: {
    flex: 1,
  },
  itemHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  itemName: {
    fontSize: 13,
    fontWeight: "700",
  },
  smallPill: {
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 4,
  },
  smallPillText: {
    fontSize: 10,
    fontWeight: "700",
  },
  itemSubtext: {
    fontSize: 11,
    marginTop: 2,
  },
  loadingContainer: {
    padding: 24,
    alignItems: "center",
    gap: 8,
  },
  loadingText: {
    fontSize: 12,
  },
  emptyContainer: {
    padding: 24,
    alignItems: "center",
  },
  emptyText: {
    fontSize: 12,
    fontStyle: "italic",
  },
  errorText: {
    color: "#DC2626",
    fontSize: 10,
    lineHeight: 12,
    marginTop: 1,
    fontWeight: "600",
  },
});
